/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';

/**
 * ValidateAddress Input Action
 */
export class ValidateAddressInput implements Msdyn365.IActionInput {
    public readonly addressLine1: string;
    public readonly addressLine2: string;
    public readonly city: string;
    public readonly state: string;
    public readonly zipcode: string;
    public readonly country: string;
    public readonly wafToken: string;

    constructor(
        addressLine1: string,
        addressLine2: string,
        city: string,
        state: string,
        zipcode: string,
        country: string,
        wafToken: string
    ) {
        this.addressLine1 = addressLine1;
        this.addressLine2 = addressLine2;
        this.city = city;
        this.state = state;
        this.zipcode = zipcode;
        this.country = country;
        this.wafToken = wafToken;
    }

    public getCacheKey = () => `${this.addressLine1}-${this.addressLine2}-${this.city}-${this.state}-${this.zipcode}-${this.country}`;
    public getCacheObjectType = () => 'VerifyAddress';
    public dataCacheType = (): Msdyn365.CacheType => 'none';
}

export interface IValidateAddressData {
    captchaRequired?: boolean;
    verifiedAddresses: IAddress[];
}

export interface IAddress {
    addressLine1: string;
    addressLine2: string;
    city: string;
    state: string;
    zipcode: string;
    country: string;
}

const createInput = (inputData: Msdyn365.ICreateActionContext & ValidateAddressInput): Msdyn365.IActionInput => {
    const { addressLine1, addressLine2, city, state, zipcode, country, wafToken: captcha } = inputData;
    return new ValidateAddressInput(addressLine1, addressLine2, city, state, zipcode, country, captcha);
};

async function action(input: ValidateAddressInput, context: Msdyn365.IActionContext): Promise<IValidateAddressData> {
    const { sstApiUrl } = context.requestContext.app.config;

    const result = await Msdyn365.sendRequest<IAddress[]>(
        `${sstApiUrl}/address/verify`,
        'post',
        JSON.stringify({
            address: {
                addressLine1: input.addressLine1,
                addressLine2: input.addressLine2,
                city: input.city,
                state: input.state,
                zipcode: input.zipcode,
                country: input.country
            }
        }),
        {
            headers: {
                'Content-Type': 'application/json',
                'x-aws-waf-token': input.wafToken
            }
        }
    );

    return {
        captchaRequired: result.status === 405,
        verifiedAddresses: result.data
    };
}

export default Msdyn365.createObservableDataAction<IValidateAddressData>({
    action: <Msdyn365.IAction<IValidateAddressData>>action,
    id: 'ValidateAddress',
    input: createInput
});
